import StringUtils from "../utils/StringUtils.js";
import StorageService from "../services/StorageService.js";
import ScoreUtils from "../utils/ScoreUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import AccountSync from "../services/AccountSync.js";
import { isBetterLetterRun } from "../services/syncMerge.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { enterEndGame, leaveEndGame } from "../utils/EndGameUI.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";

export default class CountryNerd {
	// mode "classic" (default) shows a placeholder per remaining country as a hint;
	// mode "hard" only reveals the countries you actually find (the original
	// behaviour) — no hint about how many are left. Each mode keeps its own
	// per-letter progress under a distinct gameId.
	constructor(mode = "classic") {
		this.mode = mode;
		this.gameId = mode === "hard" ? "countrynerdhard" : "countrynerd";
		this.currentLetter = "a";
		this.countriesFound = 0;

		this.page = mode === "hard"
			? document.querySelector(".page.country-nerd-hard")
			: document.querySelector(".page.country-nerd:not(.country-nerd-hard)");
		if (!this.page) return;

		this.answers = this.page.querySelector(".answers");
		this.answerContainer = this.page.querySelector(".answer-container");
		this.answerInput = this.page.querySelector("#country-answer-input");
		this.answerButton = this.page.querySelector("#country-answer-validate");
		this.letters = this.page.querySelectorAll(".letter");
		this.wonLink = this.page.querySelector(".won-link");
		this.tipCurrent = this.page.querySelector(".tip .found");
		this.tipTotal = this.page.querySelector(".tip .total");
		this.hintDisplay = this.page.querySelector(".hint-display");
		this.lettersTotal = this.page.querySelector(".letters-total");

		this.buildLetters();
		this.buildInputs();
	}

	/**
	 * Secondes écoulées sur la lettre en cours, reprises comprises. Tronquées
	 * comme un chronomètre : arrondies, l'affichage et le résultat enregistré
	 * pouvaient différer d'une seconde à l'arrivée.
	 */
	letterElapsedSeconds(letter = this.currentLetter) {
		const startedAt = StorageService.load(`${this.gameId}.letterStart.${letter}`);
		if (!startedAt) return null;
		return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
	}

	/**
	 * Chrono de la lettre en cours, affiché à côté du compteur de pays.
	 *
	 * La durée était mesurée et classée sans jamais être montrée : on ne
	 * découvrait son temps qu'une fois la lettre bouclée, trop tard pour se
	 * décider à accélérer.
	 */
	startLetterTicker() {
		this.stopLetterTicker();
		const tip = this.page.querySelector(".tip");
		if (!tip) return;

		// Dans la pilule du compteur, comme le chrono des autres modes vit dans leur
		// pilule de score : posés à côté, l'heure et les fautes flottaient sans
		// rattachement visuel.
		if (!tip.querySelector(".elapsed")) {
			tip.insertAdjacentHTML("beforeend",
				`<span class="elapsed"><span class="elapsed-icon" aria-hidden="true"></span><span class="elapsed-value"></span></span>` +
				`<span class="letter-faults hidden" aria-label="${geoNerdApp.languageManager.t("leaderboard.errors")}"></span>`);
		}
		this.letterElapsedEl = tip.querySelector(".elapsed-value");
		this.letterFaultsEl = tip.querySelector(".letter-faults");
		this.renderLetterFaults();

		const tick = () => {
			const seconds = this.letterElapsedSeconds();
			this.letterElapsedEl.textContent = seconds === null ? "" : formatDuration(seconds);
		};
		tick();
		this.letterTimer = setInterval(tick, 1000);
	}

	stopLetterTicker() {
		if (!this.letterTimer) return;
		clearInterval(this.letterTimer);
		this.letterTimer = null;
	}

	loadProgress(letter) {
		const saved = StorageService.load(`${this.gameId}.progress.${letter}`, []);
		return Array.isArray(saved) ? saved : [];
	}

	/**
	 * Démarre la mesure d'une lettre : chrono et compteur de fautes. Les deux sont
	 * persistés dès le premier instant, comme les vies des modes à vies, pour qu'un
	 * rechargement ne remette ni le chrono à zéro ni les fautes à néant.
	 *
	 * Ces clés restent locales : seul le résultat consolidé (`letterRun`) part à la
	 * synchro, une fois la lettre bouclée. Rien n'est mesuré sur une lettre déjà
	 * terminée : son résultat est acquis.
	 */
	startLetterRun(letter) {
		if (StorageService.load(`${this.gameId}.letterRun.${letter}`)) return;
		if (!StorageService.load(`${this.gameId}.letterStart.${letter}`)) {
			StorageService.save(`${this.gameId}.letterStart.${letter}`, Date.now());
		}
	}

	/**
	 * Rejoue une lettre déjà bouclée pour tenter un meilleur temps.
	 *
	 * Rien n'est effacé : la progression et le drapeau de complétion restent en place,
	 * seul l'affichage repart de zéro. Un rejeu abandonné en cours de route ne coûte
	 * donc rien, et il n'y a pas à supprimer les clés côté serveur, ce qu'un simple
	 * effacement local aurait exigé (le drapeau est monotone au merge et la
	 * progression une union : elles seraient revenues au prochain pull).
	 *
	 * À l'arrivée, `saveLetterRun()` ne garde le nouveau résultat que s'il est meilleur.
	 */
	replayLetter() {
		const letter = this.currentLetter;
		const countries = geoNerdApp.countriesByLetter[letter];
		if (!countries) return;

		this.replaying = true;
		this.finished = false;
		this.countriesFound = 0;
		countries.forEach(country => (country.found = false));

		StorageService.save(`${this.gameId}.letterStart.${letter}`, Date.now());
		StorageService.remove(`${this.gameId}.letterFaults.${letter}`);
		StorageService.remove(`${this.gameId}.letterHints.${letter}`);

		this.answers.innerHTML = "";
		this.setAnswerColumns(countries.length);
		this.answerInput.value = "";
		this.answerContainer.classList.remove("win");
		this.wonLink.classList.remove("show");
		this.wonLink.classList.add("hidden");

		if (this.mode !== "hard") {
			let placeholderIndex = 0;
			this.sortedCountries.forEach(() => this.answers.appendChild(this.buildPlaceholderEl(placeholderIndex++)));
		}
		this.answers.insertAdjacentHTML("afterbegin", `<div class="answer answer-letter">${letter}</div>`);
		this.tipCurrent.innerHTML = 0;
		const result = this.page.querySelector(".letter-result");
		if (result) result.remove();

		const shareBtn = this.page.querySelector(".btn-share");
		const backBtn = this.page.querySelector(".btn-back-to-letters");
		const replayBtn = this.page.querySelector(".btn-replay-letter");
		if (shareBtn) shareBtn.classList.add("hidden");
		if (backBtn) backBtn.classList.remove("hidden");
		if (replayBtn) replayBtn.classList.add("hidden");

		// Un rejeu est une manche comme une autre : son chrono repart (il était
		// resté figé sur le temps précédent) et l'indice redevient disponible. Il se
		// paie toujours d'une faute, et `saveLetterRun()` ne garde que le meilleur
		// résultat : s'aider dégrade la tentative au lieu de gonfler le record.
		leaveEndGame(this.page);
		this.startLetterTicker();
		this.setHintAvailable(true);

		this.answerInput.focus({ preventScroll: true });
	}

	/**
	 * Révèle une lettre de plus du premier pays encore à trouver, au prix d'une
	 * faute sur la lettre en cours (les fautes pèsent au classement).
	 *
	 * Une lettre à 22 pays où l'on bloque à 20 n'avait aucune issue : ni indice, ni
	 * abandon, ni moyen de savoir ce qui manquait. L'indice débloque sans donner la
	 * réponse, et son coût le garde honnête vis-à-vis de ceux qui trouvent seuls.
	 */
	revealHint() {
		if (this.finished || !this.currentLetter) return;
		const next = this.sortedCountries.find(c => !c.found);
		if (!next) return;

		const hints = this.loadHints();
		// On part de la deuxième lettre : la première est celle de la grille, le
		// joueur la connaît avant même de cliquer. La lui vendre serait un vol.
		const already = hints[next.sanitize] || 1;
		const revealed = Math.min(already + 1, next.name.length);
		if (revealed === already) return;

		hints[next.sanitize] = revealed;
		this.saveHints(hints);
		this.recordLetterFault();

		this.renderHint();
	}

	/** Affiche le préfixe déjà payé du prochain pays à trouver. */
	renderHint() {
		if (!this.hintDisplay) return;
		const next = this.sortedCountries ? this.sortedCountries.find(c => !c.found) : null;
		const revealed = next ? (this.loadHints()[next.sanitize] || 0) : 0;

		if (!next || revealed === 0) {
			this.hintDisplay.textContent = "";
			return;
		}

		const prefix = next.name.slice(0, revealed);
		const rest = "·".repeat(Math.max(0, next.name.replace(/\s/g, "").length - revealed));
		this.hintDisplay.innerHTML = `<strong>${prefix}</strong>${rest}`;
	}

	/**
	 * Compteur de fautes de la lettre en cours.
	 *
	 * Elles étaient comptées et pesaient au classement sans jamais s'afficher —
	 * et depuis que l'indice s'en paie une, ne pas les montrer revenait à faire
	 * payer un prix invisible.
	 */
	renderLetterFaults() {
		if (!this.letterFaultsEl) return;
		const faults = parseInt(StorageService.load(`${this.gameId}.letterFaults.${this.currentLetter}`, 0), 10) || 0;
		this.letterFaultsEl.textContent = faults > 0 ? `✗ ${faults}` : "";
		this.letterFaultsEl.classList.toggle("hidden", faults === 0);
	}

	loadHints() {
		const saved = StorageService.load(`${this.gameId}.letterHints.${this.currentLetter}`);
		return saved && typeof saved === "object" ? saved : {};
	}

	saveHints(hints) {
		StorageService.save(`${this.gameId}.letterHints.${this.currentLetter}`, hints);
	}

	/** Une mauvaise réponse de plus sur la lettre en cours, persistée aussitôt. */
	recordLetterFault() {
		const key = `${this.gameId}.letterFaults.${this.currentLetter}`;
		const faults = parseInt(StorageService.load(key, 0), 10) || 0;
		StorageService.save(key, faults + 1);
		this.renderLetterFaults();
	}

	/**
	 * Consolide la lettre terminée en un résultat synchronisable : temps et fautes.
	 * `time: null` quand la lettre a été commencée avant que la mesure n'existe : le
	 * classement traite alors le run comme non chronométré plutôt que d'inventer une
	 * durée. Le meilleur des deux résultats est conservé si la lettre est refaite.
	 */
	saveLetterRun(letter) {
		const errors = parseInt(StorageService.load(`${this.gameId}.letterFaults.${letter}`, 0), 10) || 0;
		// Même source que le chrono affiché, pour que le résultat annoncé soit
		// exactement celui qu'on venait de lire.
		const time = this.letterElapsedSeconds(letter);
		const candidate = { time, errors };

		const previous = StorageService.load(`${this.gameId}.letterRun.${letter}`);
		const improved = !previous || isBetterLetterRun(candidate, previous);
		if (improved) {
			StorageService.save(`${this.gameId}.letterRun.${letter}`, candidate);
		}
		StorageService.remove(`${this.gameId}.letterStart.${letter}`);
		StorageService.remove(`${this.gameId}.letterFaults.${letter}`);
		StorageService.remove(`${this.gameId}.letterHints.${letter}`);
		this.replaying = false;
		AccountSync.scheduleSync();
		return { candidate, previous, improved };
	}

	/**
	 * Annonce le chrono de la lettre qu'on vient de boucler, et le record quand il
	 * vient de tomber : c'est ce qui donne envie de refaire la lettre suivante.
	 * @param {{candidate: object, previous: object|null, improved: boolean}} result
	 */
	showLetterResult({ candidate, previous, improved }) {
		if (typeof candidate.time !== "number") return;
		const lang = geoNerdApp.languageManager;
		const isRecord = improved && previous && typeof previous.time === "number";
		const label = isRecord ? lang.t("game.newBestTime") : "";

		let el = this.page.querySelector(".letter-result");
		if (!el) {
			el = document.createElement("p");
			el.className = "letter-result";
			this.answerContainer.insertBefore(el, this.answers);
		}
		el.classList.toggle("is-record", !!isRecord);
		el.innerHTML = `${label} <strong>${formatDuration(candidate.time)}</strong>`;
	}

	/**
	 * Propose de refaire la lettre, avec le temps à battre quand il y en a un. Une
	 * lettre bouclée avant la mesure n'en a pas : c'est justement l'occasion de s'en
	 * donner un.
	 */
	showReplayButton(letter) {
		const btn = this.page.querySelector(".btn-replay-letter");
		if (!btn) return;
		const lang = geoNerdApp.languageManager;
		const run = StorageService.load(`${this.gameId}.letterRun.${letter}`);
		const best = run && typeof run.time === "number"
			? ` (${lang.t("game.bestTime")} ${formatDuration(run.time)})`
			: "";
		btn.innerHTML = `${lang.t("game.replayLetter")}${best}`;
		btn.classList.remove("hidden");
	}

	/** Abandonne un rejeu en cours : le chrono et les fautes du rejeu sont jetés. */
	discardReplay() {
		if (!this.replaying) return;
		StorageService.remove(`${this.gameId}.letterStart.${this.currentLetter}`);
		StorageService.remove(`${this.gameId}.letterFaults.${this.currentLetter}`);
		this.replaying = false;
	}

	/**
	 * Nombre de pays de la lettre réellement trouvés, d'après la sauvegarde. On ne
	 * compte que les entrées qui correspondent encore à un pays de la lettre,
	 * exactement comme restoreLetterProgress(), pour qu'un pays renommé ou retiré
	 * des données ne puisse pas gonfler le compteur au-delà du total.
	 */
	countFoundFromStorage(letter, countries) {
		const saved = new Set(this.loadProgress(letter));
		return countries.filter(c => saved.has(c.sanitize)).length;
	}

	saveProgress(letter) {
		// Pendant un rejeu, la progression persistée est déjà complète : la réécrire
		// depuis l'état en cours (qui repart de zéro) l'effacerait.
		if (this.replaying) return;
		const foundCountries = geoNerdApp.countriesByLetter[letter]
			.filter(c => c.found)
			.map(c => c.sanitize);
		StorageService.save(`${this.gameId}.progress.${letter}`, foundCountries);
		AccountSync.scheduleSync();
	}

	restoreLetterProgress(letter) {
		const savedProgress = this.loadProgress(letter);
		const countries = geoNerdApp.countriesByLetter[letter];

		this.countriesFound = 0;
		this.finished = false;
		this.replaying = false;
		this.answers.innerHTML = '';
		this.setAnswerColumns(countries.length);
		this.answerInput.value = "";
		this.answerContainer.classList.remove("win");
		this.wonLink.classList.remove("show", "hidden");
		this.wonLink.classList.add("hidden");

		const shareBtn = this.page.querySelector(".btn-share");
		const backBtn = this.page.querySelector(".btn-back-to-letters");
		const replayBtn = this.page.querySelector(".btn-replay-letter");
		if (shareBtn) shareBtn.classList.add("hidden");
		if (backBtn) backBtn.classList.remove("hidden");
		if (replayBtn) replayBtn.classList.add("hidden");

		countries.forEach(country => country.found = false);

		savedProgress.forEach(savedSanitize => {
			const country = countries.find(c => c.sanitize === savedSanitize);
			if (country && !country.found) {
				country.found = true;
				this.countriesFound++;
			}
		});

		this.sortedCountries = [...countries].sort((a, b) => a.sanitize.localeCompare(b.sanitize));
		if (this.mode === "hard") {
			// Hard: reveal only the found countries, sorted alphabetically — no hint
			// about the ones left to find.
			this.sortedCountries
				.filter(country => country.found)
				.forEach(country => this.answers.appendChild(this.buildAnswerEl(country)));
		} else {
			// Classic: render the full alphabetical list up-front, each country shown
			// either as a found answer or as an empty placeholder, so found countries
			// keep their exact alphabetical position among the slots still to fill.
			let placeholderIndex = 0;
			this.sortedCountries.forEach(country => {
				this.answers.appendChild(
					country.found ? this.buildAnswerEl(country) : this.buildPlaceholderEl(placeholderIndex++)
				);
			});
		}

		this.tipCurrent.innerHTML = this.countriesFound;
		this.tipTotal.innerHTML = countries.length;

		if (this.countriesFound < countries.length) {
			this.answers.insertAdjacentHTML("afterbegin", `<div class="answer answer-letter">${letter}</div>`);
			this.startLetterRun(letter);
			this.startLetterTicker();
			this.setHintAvailable(true);
		} else {
			this.finished = true;
			this.stopLetterTicker();
			this.setHintAvailable(false);
			this.wonLink.classList.add("show");
			this.wonLink.classList.remove("hidden");
			this.answerContainer.classList.add("win");
			if (backBtn) backBtn.classList.add("hidden");
			this.initShare(shareBtn, countries.length);
			this.showReplayButton(letter);
		}
	}

	/**
	 * Sur grand écran, la liste passe en colonnes (voir `_country-nerd.scss`).
	 * Leur nombre suit la taille de la lettre, environ six pays par colonne :
	 * un nombre fixe aurait étalé les trois pays du J sur toute la largeur. Calculé
	 * sur le total de la lettre, pas sur les pays trouvés, pour que la mise en page
	 * ne bouge pas en cours de partie (le total est de toute façon affiché).
	 */
	setAnswerColumns(total) {
		this.answers.style.setProperty("--answer-columns", Math.min(4, Math.ceil(total / 6)));
	}

	buildAnswerEl(country) {
		const el = document.createElement("div");
		el.className = "answer valid";
		el.dataset.sanitize = country.sanitize;
		el.innerHTML = `${country.name}<span class="flag" style="background-image: url(${getFlagPath(country.code)})"></span>`;
		return el;
	}

	// Empty slot for a country still to be found: it tells the player how many
	// countries remain for the current letter without revealing any answer.
	buildPlaceholderEl(index = 0) {
		const widths = ["6rem", "9rem", "7rem", "11rem", "8rem"];
		const el = document.createElement("div");
		el.className = "answer placeholder";
		el.setAttribute("aria-hidden", "true");
		el.innerHTML = `<span class="placeholder-name" style="width:${widths[index % widths.length]}"></span><span class="placeholder-flag"></span>`;
		return el;
	}

	// Reveal a found country at its correct alphabetical position.
	// Classic: replace the placeholder at the country's rank (slots are always the
	// full sorted list, so the slot at that rank is its placeholder).
	// Hard: insert among the existing found answers, keeping them sorted.
	placeFoundCountry(country) {
		const el = this.buildAnswerEl(country);
		if (this.mode === "hard") {
			const valids = [...this.answers.querySelectorAll(".answer.valid")];
			const next = valids.find(v => v.dataset.sanitize.localeCompare(country.sanitize) > 0);
			if (next) {
				this.answers.insertBefore(el, next);
			} else {
				this.answers.appendChild(el);
			}
			return el;
		}

		const rank = this.sortedCountries.findIndex(c => c.sanitize === country.sanitize);
		const slots = this.answers.querySelectorAll(".answer.valid, .answer.placeholder");
		const target = slots[rank];
		if (target) {
			this.answers.replaceChild(el, target);
		} else {
			this.answers.appendChild(el);
		}
		return el;
	}

	updateLetterIndicators() {
		let foundOverall = 0;
		let totalOverall = 0;
		let lettersDone = 0;
		let lettersPlayable = 0;

		this.letters.forEach(letterEl => {
			const letterText = letterEl.dataset.letter;
			if (!letterText || letterEl.classList.contains("disabled")) return;

			const countries = geoNerdApp.countriesByLetter[letterText];
			if (!countries) return;

			const found = this.countFoundFromStorage(letterText, countries);
			const total = countries.length;
			foundOverall += found;
			totalOverall += total;
			lettersPlayable++;
			if (total > 0 && found === total) lettersDone++;

			// La complétion se déduit de l'état courant (found/total), jamais du seul
			// flag `{gameId}.letter.{lettre}` : si les données de la lettre changent
			// après coup (pays ajouté, nom corrigé), le flag reste à true et la tuile
			// s'affichait "complétée" à côté de son propre "5/6", en désaccord avec
			// restoreLetterProgress(), qui rouvre bien la lettre en mode "à finir".
			// Le flag persisté n'est pas effacé pour autant : c'est le score de la
			// lettre (compté par la page Scores, le classement et l'admin) et le merge
			// de sync le garde monotone (mergeLetterFlag), donc un retrait local
			// reviendrait au prochain pull. Finir le pays manquant le réécrira.
			const isCompleted = total > 0 && found === total;
			letterEl.classList.toggle("completed", isCompleted);

			let indicator = letterEl.querySelector(".letter-progress") || document.createElement("span");
			if (!indicator.parentNode) {
				indicator.className = "letter-progress";
				letterEl.appendChild(indicator);
			}

			indicator.textContent = `${found}/${total}`;
			letterEl.classList.toggle("in-progress", found > 0 && !isCompleted);

			// Meilleur temps de la lettre, affiché sur la tuile : c'est le seul endroit
			// où l'on voit d'un coup d'œil ce qu'il reste à améliorer une fois l'alphabet
			// bouclé. Une lettre finie avant la mesure n'en a pas : elle est justement
			// celle qu'il y a le plus à gagner à refaire.
			const run = isCompleted ? StorageService.load(`${this.gameId}.letterRun.${letterText}`) : null;
			let time = letterEl.querySelector(".letter-time");
			if (run && typeof run.time === "number") {
				if (!time) {
					time = document.createElement("span");
					time.className = "letter-time";
					letterEl.appendChild(time);
				}
				time.textContent = formatDuration(run.time);
			} else if (time) {
				time.remove();
			}
		});

		this.renderLettersTotal(foundOverall, totalOverall, lettersDone, lettersPlayable);
	}

	/**
	 * Récapitule la collection sous la grille. Chaque tuile disait déjà « 3/14 »,
	 * mais rien ne donnait l'avancement réel : c'est pourtant le seul mode dont la
	 * promesse est de tous les avoir.
	 */
	renderLettersTotal(found, total, lettersDone, lettersPlayable) {
		if (!this.lettersTotal || total === 0) return;
		const lang = geoNerdApp.languageManager;
		this.lettersTotal.innerHTML =
			`<strong>${found}</strong>/${total} ${lang.t("game.countriesTotal")}` +
			` · <strong>${lettersDone}</strong>/${lettersPlayable} ${lang.t("game.lettersDone")}`;
	}

	buildLetters() {
		const letterPlaceholder = this.page.querySelector(".letter-placeholder");
		const mainTitle = this.page.querySelector("h1");
		const explanation = this.page.querySelector(".explanation");
		const gameTitle = this.page.querySelector(".game-title");
		const lettersContainer = this.page.querySelector(".letters");

		this.letters.forEach(letter => {
			letter.dataset.letter = letter.textContent.trim().charAt(0).toLowerCase();
		});

		this.updateLetterIndicators();

		this.letters.forEach(letter => {
			const letterText = letter.dataset.letter;
			letter.addEventListener("click", () => {
				if (letter.classList.contains("disabled")) return;

				this.currentLetter = letterText;
				lettersContainer.classList.add("hide");
				gsap.to([mainTitle, explanation], { opacity: 0, pointerEvents: "none", delay: 0.2 });
				gsap.to(gameTitle, { opacity: 1, delay: 0.5 });
				letterPlaceholder.innerHTML = letterText;

				this.restoreLetterProgress(letterText);

				setTimeout(() => {
					lettersContainer.style.display = "none";
					// Le total suit la grille : dans une lettre, il n'a plus rien à dire
					// et viendrait se coller au-dessus du champ de saisie.
					if (this.lettersTotal) this.lettersTotal.style.display = "none";
					this.answerContainer.style.display = "flex";
					gsap.to(this.answerContainer, { opacity: 1, delay: 0.1 });
					if (!this.finished) this.answerInput.focus();
				}, 400);
			});
		});
	}

	buildInputs() {
		this.answerInput.addEventListener("keydown", e => {
			// preventDefault stops the mobile browser's default action on the
			// virtual keyboard's Enter/Go key (which otherwise blurs the field
			// and closes the keyboard).
			if (e.key === "Enter") {
				e.preventDefault();
				this.validateAnswer(this.answerInput.value);
				this.keepKeyboardOpen();
			}
		});
		const submit = () => {
			this.validateAnswer(this.answerInput.value);
			this.keepKeyboardOpen();
		};
		// Act on touchstart (earliest touch event) + preventDefault: iOS steals
		// the first tap on a control while the keyboard is open to dismiss it
		// (firing touchcancel, not click/touchend), so those never run.
		// touchstart fires before that; preventDefault keeps focus on the input
		// (keyboard stays) and suppresses the emulated click (no double-submit).
		this.answerButton.addEventListener("touchstart", e => { e.preventDefault(); submit(); }, { passive: false });
		this.answerButton.addEventListener("click", submit);
		const backButton = this.page.querySelector(".btn-back-to-letters");
		if (backButton) backButton.addEventListener("click", () => this.backToLetters());
		if (this.wonLink) this.wonLink.addEventListener("click", () => this.backToLetters());
		const replayButton = this.page.querySelector(".btn-replay-letter");
		if (replayButton) replayButton.addEventListener("click", () => this.replayLetter());
		this.hintButton = this.page.querySelector(".btn-hint");
		if (this.hintButton) {
			this.hintButton.addEventListener("click", () => {
				const useHint = () => {
					this.revealHint();
					this.answerInput.focus({ preventScroll: true });
				};

				// Avertissement une seule fois (pas à chaque indice) : passé le premier
				// clic confirmé, le joueur sait déjà ce qu'il paie.
				const warnedKey = `geonerd.${this.gameId}.hintWarned`;
				if (localStorage.getItem(warnedKey) === "true") {
					useHint();
					return;
				}

				const lang = geoNerdApp.languageManager;
				showConfirmation(
					lang.t("settings.confirmTitle"),
					lang.t("game.hintConfirmMessage"),
					() => {
						localStorage.setItem(warnedKey, "true");
						useHint();
					}
				);
			});
		}
	}

	/** Le bouton d'indice n'a de sens que sur une lettre encore ouverte. */
	setHintAvailable(available) {
		const control = this.page.querySelector(".hint-control");
		if (control) control.classList.toggle("hidden", !available);
		else if (this.hintButton) this.hintButton.classList.toggle("hidden", !available);
		this.renderHint();
	}

	// Re-focus the input synchronously, inside the user gesture, so the mobile
	// keyboard stays/re-opens between guesses (Android closes it on the
	// keyboard's Enter/Go key even with preventDefault). No-op once the letter
	// is complete (the input is replaced by the win UI).
	keepKeyboardOpen() {
		if (!this.finished) this.answerInput.focus({ preventScroll: true });
	}

	validateAnswer(answer) {
		// Valider à vide ne coûte rien : sans réponse il n'y a pas de faute à
		// sanctionner, et les fautes de la lettre pèsent au classement (letterFaults).
		// Même règle que Flag Nerd Hard, Map Nerd Hard et le Défi du jour hard.
		if (!answer || !answer.trim()) return;
		// Match against the closest not-yet-found country of the letter. bestMatch's
		// `ambiguous` guard refuses near-ties (e.g. Iran vs Iraq) so a typo can't
		// silently fill in the wrong country.
		const pool = geoNerdApp.countriesByLetter[this.currentLetter].filter(c => !c.found);
		const { item, score, ambiguous } = StringUtils.bestMatch(answer, pool);
		const win = !!item && score >= 0.78 && !ambiguous;

		// Un pays déjà trouvé retapé n'est plus dans le pool ci-dessus, donc
		// `bestMatch` ne peut jamais le retrouver et le prendrait pour une
		// faute. On le détecte à part et on l'ignore sans sanction, même
		// correctif que Us States List.
		if (!win) {
			const foundPool = geoNerdApp.countriesByLetter[this.currentLetter].filter(c => c.found);
			const alreadyFound = StringUtils.bestMatch(answer, foundPool);
			if (alreadyFound.item && alreadyFound.score >= 0.78 && !alreadyFound.ambiguous) {
				this.answerInput.value = "";
				return;
			}
		}

		if (win) {
			this.answerInput.value = "";
			item.found = true;
			const answerEl = this.placeFoundCountry(item);
			SoundManager.correct();
			FeedbackEffects.confetti(answerEl);
			this.countriesFound++;
			this.tipCurrent.innerHTML = this.countriesFound;

			if (this.countriesFound === geoNerdApp.countriesByLetter[this.currentLetter].length) {
				this.finished = true;
			}

			this.saveProgress(this.currentLetter);
			// L'indice suit la progression : il repart sur le prochain pays manquant.
			this.renderHint();

			gsap.to(this.answerInput, {
				backgroundColor: "#25961c",
				duration: 0.2,
				onComplete: () => gsap.to(this.answerInput, { backgroundColor: "white", duration: 0.2 })
			});

			if (this.finished) {
				// All found: drop the "current letter" hint + any leftover placeholders.
				this.answers.querySelectorAll(".answer:not(.valid)").forEach(el => el.remove());
				this.stopLetterTicker();
				this.setHintAvailable(false);
				enterEndGame(this.page);
				SoundManager.victory();
				const backBtn = this.page.querySelector(".btn-back-to-letters");
				if (backBtn) backBtn.classList.add("hidden");
				this.wonLink.classList.add("show");
				this.answerContainer.classList.add("win");
				StorageService.save(`${this.gameId}.letter.${this.currentLetter}`, true);
				const letterRun = this.saveLetterRun(this.currentLetter);
				// Une lettre terminée = une « partie » de Country Nerd, comme dans
				// les statistiques du back-office.
				ScoreUtils.recordPlay(this.gameId);
				const shareBtn = this.page.querySelector(".btn-share");
				this.initShare(shareBtn, geoNerdApp.countriesByLetter[this.currentLetter].length, letterRun.candidate);
				this.showLetterResult(letterRun);
				this.showReplayButton(this.currentLetter);
			}
		} else {
			this.recordLetterFault();
			SoundManager.wrong();
			FeedbackEffects.shake(this.page.querySelector(".input-container"));
			gsap.to(this.answerInput, {
				backgroundColor: "#F05050",
				duration: 0.2,
				onComplete: () => gsap.to(this.answerInput, { backgroundColor: "white", duration: 0.2 })
			});
			this.answerInput.value = "";
		}
	}

	/**
	 * @param {{time: number|null, errors: number}} [run] Résultat de la lettre
	 * (voir `saveLetterRun()`) : pas de vies ici, donc le score partagé porte
	 * les fautes et le temps, mêmes informations que le classement des lettres
	 * (`isLetters` dans Leaderboard.js). `time: null` (lettre commencée avant
	 * la mesure) omet simplement le temps plutôt que d'en inventer un.
	 */
	initShare(btn, total, run) {
		if (!btn) return;
		const isHard = this.mode === "hard";
		btn.classList.remove("hidden");
		btn.addEventListener("click", () => {
			const lang = geoNerdApp.languageManager;
			const errors = run?.errors ?? 0;
			const errorsText = `✗${errors} ${lang.t("leaderboard.errors").toLowerCase()}`;
			const timeText = typeof run?.time === "number" ? ` · ⏱${formatDuration(run.time)}` : "";
			geoNerdApp.shareResult(
				btn,
				geoNerdApp.languageManager.t("home.countryNerd"),
				`Lettre ${this.currentLetter.toUpperCase()} ${isHard ? "Hard" : "Classic"}`,
				`${this.countriesFound}/${total} · ${errorsText}${timeText}`,
				isHard ? "🟥" : "🟦",
				isHard ? "country-nerd-hard" : "country-nerd"
			);
		});
	}

	backToLetters() {
		this.discardReplay();
		this.stopLetterTicker();
		leaveEndGame(this.page);
		const mainTitle = this.page.querySelector("h1");
		const explanation = this.page.querySelector(".explanation");
		const gameTitle = this.page.querySelector(".game-title");
		const lettersContainer = this.page.querySelector(".letters");

		gsap.to(this.answerContainer, {
			opacity: 0,
			duration: 0.2,
			onComplete: () => {
				this.answerContainer.style.display = "none";
				lettersContainer.style.display = "flex";
				if (this.lettersTotal) this.lettersTotal.style.display = "";
				setTimeout(() => lettersContainer.classList.remove("hide"), 10);
				this.answerInput.value = "";
				this.updateLetterIndicators();
				gsap.to([mainTitle, explanation], { opacity: 1, pointerEvents: "all", duration: 0.4 });
				gsap.to(gameTitle, { opacity: 0, duration: 0.2 });
			}
		});
	}
}
